/* §xpand-ai-orb (2026-07-27) — the animated XPAND orb, used as the nav icon for
 * the XPAND AI entry in place of the old Sparkles glyph.
 *
 * The source is a 608px ProRes 4444 render with a real alpha channel. It is
 * re-encoded to two web formats because no single one covers both engines:
 * VP9-with-alpha (.webm) for Chrome, Firefox and Edge, HEVC-with-alpha (.mov)
 * for Safari. Each browser picks the one it can decode and ignores the other,
 * so nobody downloads both. The PNG is the poster and the fallback.
 *
 * The clip is one full period of the animation (frames 0-447 of the source, 15.1s
 * at 15fps): frame 448 matches frame 0 to within 0.99/255, well under the 4.18
 * median frame-to-frame motion, so the loop has no visible seam.
 *
 * Note for anyone verifying the assets: `ffprobe` reports BOTH files as
 * `yuv420p` whatever their real alpha. VP9 and HEVC each keep alpha in a layer
 * the stream summary does not surface, so the field tells you nothing here.
 *
 * To actually check, read the PIXELS — decode frame 0 to RGBA and look at a
 * corner:
 *     ffmpeg -i orb-icon.mov -vframes 1 -pix_fmt rgba /tmp/f.png
 *     python3 -c "from PIL import Image; print(Image.open('/tmp/f.png').convert('RGBA').getpixel((0,0)))"
 * Alpha 0 = transparent, 255 = an opaque box.
 *
 * §orb-safari-alpha (2026-07-28) — that ffprobe blind spot hid a real bug for a
 * day: the sources were ordered webm-first, Safari (which now supports WebM)
 * took the webm, and Safari's VP9 ignores alpha — so the orb was a BLACK BOX on
 * the light /ask page while looking perfect in Chrome. The .mov is first now
 * and the order must stay that way; see the comment at the <source> tags.
 */
import { useEffect, useRef } from "react";

type Props = {
  className?: string;
  size?: number;
  /** "icon" is the 64px encode for the 16px nav glyph; "lg" is the 192px encode
   *  for the 88px orb on the XPAND AI page. Two sizes rather than one, because
   *  the nav icon loads on every page and should stay small, while the large orb
   *  only loads on /ask. */
  variant?: "icon" | "lg";
  "aria-hidden"?: boolean | "true" | "false";
};

/** Matches the call signature of the lucide icons it replaces, so it drops
 *  straight into the NAV tables with no change at the render site. */
export default function OrbIcon({ className, size, variant = "icon", ...rest }: Props) {
  const ref = useRef<HTMLVideoElement>(null);
  const src = variant === "lg" ? "/orb-lg" : "/orb-icon";

  useEffect(() => {
    const v = ref.current;
    if (!v) return;

    // Someone who asked for less motion gets the still frame.
    const calm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (calm.matches) v.pause();
      else void v.play().catch(() => {});
    };
    apply();
    calm.addEventListener("change", apply);

    // The sidebar is on every page, so don't decode video for a tab nobody
    // is looking at.
    const onVis = () => {
      if (document.hidden) v.pause();
      else if (!calm.matches) void v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      calm.removeEventListener("change", apply);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <video
      ref={ref}
      className={className}
      width={size}
      height={size}
      // Without an explicit size the video fills its container, which is how the
      // large orb inherits the 88px / 30px from .xpai-orb.
      style={
        size
          ? { width: size, height: size, objectFit: "contain", background: "none" }
          : { width: "100%", height: "100%", objectFit: "contain", background: "none" }
      }
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={`${src}.png`}
      {...rest}
    >
      {/* §orb-safari-alpha (2026-07-28) — ORDER IS LOAD-BEARING. The .mov MUST
        * come first.
        *
        * These used to be webm-then-mov, on the assumption that Safari would
        * skip the webm and fall through to HEVC. That assumption is out of
        * date: modern Safari DOES support WebM/VP9, so it took the first
        * source — and Safari's VP9 does not honour the alpha channel, so the
        * orb rendered as a BLACK BOX on the light /ask background. Chrome and
        * Firefox honour VP9 alpha, which is why it looked fine everywhere I
        * tested and broke on the one browser I didn't.
        *
        * A browser picks the FIRST source whose type it can play, so putting
        * the HEVC-alpha .mov first hands Safari the file that actually has
        * working transparency; Chrome/Firefox can't play video/quicktime and
        * fall through to the webm as before.
        *
        * Verified by PIXELS, not metadata: decode frame 0 to RGBA and read the
        * corner alpha — the .mov gives 0 (transparent), the .webm gives 255.
        * Do NOT re-check this with ffprobe. It reports BOTH files as yuv420p
        * regardless, because neither VP9 nor HEVC exposes its alpha layer in
        * the stream summary. That blind spot is exactly what hid this bug. */}
      <source src={`${src}.mov`} type='video/quicktime; codecs="hvc1"' />
      <source src={`${src}.webm`} type="video/webm" />
    </video>
  );
}
